import React, { useState, useRef, useEffect } from 'react';
import styles from './style.module.css';
import api from '../../api';

const BADGES = [
    { title: 'First Light', icon: '☀', date: 'MAR 14, 2026', caption: 'Completed your very first morning ritual.', color: '#d8783c', code: '001' },
    { title: 'Trailblazer', icon: '⌁', date: 'APR 02, 2026', caption: 'Made a new path and kept moving forward.', color: '#657c6b', code: '002' },
    { title: 'Deep Focus', icon: '◉', date: 'APR 21, 2026', caption: 'Found your flow for seven days in a row.', color: '#455d68', code: '003' },
    { title: 'Kindred Spirit', icon: '♥', date: 'MAY 08, 2026', caption: 'Shared encouragement with your community.', color: '#a85c51', code: '004' },
    { title: 'Night Owl', icon: '☾', date: 'JUN 17, 2026', caption: 'Turned a late-night idea into something real.', color: '#545271', code: '005' },
    { title: 'High Tide', icon: '≈', date: 'AUG 29, 2026', caption: 'Came back stronger after a challenging week.', color: '#397b81', code: '006' }
];

export let refresh = () => { };

export default function Carousel(props) {
    const [activeIndex, setActiveIndex] = useState(0);
    const [badges, setBadges] = useState([]);
    const carouselRef = useRef(null);
    const cardsRef = useRef([]);
    const carouselSectionRef = useRef(null);

    const fetch = async () => {
        const earned = await api.geo.earned();
        earned.push({ title: 'Newbie', icon: '☀', earned: new Date(), caption: 'Thanks for joining AMP rangers!.', color: '#d8783c' });
        setBadges(earned);
    };

    useEffect(() => { fetch() }, [props.fetchData]);

    useEffect(() => {
        refresh = () => { fetch() };
    }, []);

    const scrollToBadge = (index) => {
        const card = cardsRef.current[index];
        if (card) {
            card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    };

    const handleListItemClick = (index) => {
        scrollToBadge(index);
        if (carouselSectionRef.current) {
            carouselSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const updateCarouselStyles = () => {
        const carousel = carouselRef.current;
        if (!carousel) return;

        const center = carousel.scrollLeft + carousel.clientWidth / 2;
        let nearest = 0;
        let nearestDistance = Infinity;

        cardsRef.current.forEach((card, index) => {
            if (!card) return;
            const cardCenter = card.offsetLeft + card.offsetWidth / 2;
            const distance = (cardCenter - center) / card.offsetWidth;
            const absolute = Math.abs(distance);

            card.style.setProperty('--scale', Math.max(0.86, 1 - absolute * 0.12));
            card.style.setProperty('--rotate', `${Math.max(-4, Math.min(4, distance * 3))}deg`);
            card.style.setProperty('--opacity', Math.max(0.5, 1 - absolute * 0.5));

            if (absolute < nearestDistance) {
                nearestDistance = absolute;
                nearest = index;
            }
        });

        if (nearest !== activeIndex) {
            setActiveIndex(nearest);
        }
    };

    useEffect(() => {
        const carousel = carouselRef.current;
        if (!carousel) return;

        let animationFrameId;
        const handleScroll = () => {
            animationFrameId = requestAnimationFrame(updateCarouselStyles);
        };

        const handleResize = () => updateCarouselStyles();

        carousel.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize);

        updateCarouselStyles();

        return () => {
            carousel.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);
        };
    }, [activeIndex, badges]);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowRight') {
            scrollToBadge(Math.min(BADGES.length - 1, activeIndex + 1));
        } else if (e.key === 'ArrowLeft') {
            scrollToBadge(Math.max(0, activeIndex - 1));
        }
    };

    const formattedIndex = String(activeIndex + 1).padStart(2, '0');
    const formattedTotal = String(BADGES.length).padStart(2, '0');

    return (
        <main className={styles.appShell}>
            <section
                className={styles.carouselSection}
                aria-label="Featured badges"
                ref={carouselSectionRef}
            >
                <div
                    className={styles.carousel}
                    ref={carouselRef}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                >
                    {badges.map((badge, index) => (
                        <article
                            key={badge.code}
                            ref={(el) => (cardsRef.current[index] = el)}
                            className={styles.badgeCard}
                            style={{ '--accent': badge.color }}
                            aria-label={`${badge.title} badge`}
                        >
                            <div className={styles.date}>
                                <span>PASSPORT</span>
                                <span>{(new Date(((badge || {}).earned || ''))).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()}</span>
                            </div>
                            <div className={styles.emblem} aria-hidden="true">
                                {badge.icon}
                            </div>
                            <span className={styles.cardLabel}>AMP RANGERS</span>
                            <h3>{badge.title}</h3>
                            <p>{badge.caption}</p>
                        </article>
                    ))}
                </div>

                <button
                    className={`${styles.carouselButton} ${styles.next}`}
                    aria-label="Next badge"
                    onClick={() => scrollToBadge(Math.min(BADGES.length - 1, activeIndex + 1))}
                >
                    →
                </button>
            </section>

            <section className={styles.collection} aria-labelledby="collection-title">
                <div className={styles.sectionHeading}>
                    <div>
                        <p className={styles.kicker}>
                            <span className={styles.kickerMark}>✦</span> EARNED
                        </p>
                        <h2 id="collection-title">Your passports</h2>
                    </div>
                </div>

                <div className={styles.badgeList}>
                    {badges.map((badge, index) => (
                        <button
                            key={badge.code}
                            className={`${styles.listItem} ${index === activeIndex ? styles.active : ''}`}
                            onClick={() => handleListItemClick(index)}
                        >
                            <span
                                className={styles.miniBadge}
                                style={{ '--accent': badge.color }}
                                aria-hidden="true"
                            >
                                {badge.icon}
                            </span>
                            <span className={styles.listCopy}>
                                <strong>{badge.title}</strong>
                                <span>{badge.caption}</span>
                            </span>
                            <span className={styles.listMeta}>
                                {badge.date ? <span>{badge.date}</span> : null}
                            </span>
                        </button>
                    ))}
                </div>
            </section>
        </main>
    );
}